/**
 * Phone Number Cleaner Utility for Syrian Mobile Numbers
 *
 * Supports formats:
 * - +9639XXXXXXXX
 * - 09XXXXXXXX
 * - 9XXXXXXXX
 * - 009639XXXXXXXX
 */

const ARABIC_TO_ENGLISH_MAP: Record<string, string> = {
  '٠': '0',
  '١': '1',
  '٢': '2',
  '٣': '3',
  '٤': '4',
  '٥': '5',
  '٦': '6',
  '٧': '7',
  '٨': '8',
  '٩': '9',
};

const SYRIAN_MOBILE_REGEX = /^(\+?963|00963|0)?9[0-9]{8}$/;

/**
 * Converts Arabic numerals (٠-٩) to English numerals (0-9)
 */
export function convertArabicToEnglishNumbers(input: string): string {
  return input.replace(/[٠-٩]/g, (digit) => ARABIC_TO_ENGLISH_MAP[digit] || digit);
}

/**
 * Removes all whitespace and Unicode directional markers from input string
 */
export function removeWhitespace(input: string): string {
  // Remove whitespace and Unicode directional formatting characters
  // These include RTL/LTR marks, zero-width characters, etc.
  return input
    .trim()
    .replace(/\s/g, '')
    .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2069]/g, '');
}

/**
 * Cleans a mobile number by:
 * 1. Removing whitespace and Unicode directional markers
 * 2. Converting Arabic numbers to English
 */
export function cleanMobileNumber(input: string): string {
  let cleaned = removeWhitespace(input);
  cleaned = convertArabicToEnglishNumbers(cleaned);
  return cleaned;
}

/**
 * Validates if a mobile number matches Syrian mobile format
 */
export function isValidSyrianMobile(input: string): boolean {
  const cleaned = cleanMobileNumber(input);
  return SYRIAN_MOBILE_REGEX.test(cleaned);
}

/**
 * Formats a Syrian mobile number to standard format
 * Returns the cleaned number as-is (backend handles formatting)
 */
export function formatSyrianMobile(input: string): string {
  return cleanMobileNumber(input);
}
